import React, { useState, useEffect, useContext } from "react";
import {
  Page,
  Layout,
  Card,
  TextField,
  FormLayout,
  Text,
  InlineCode,
  Select,
  Banner,
  Stack,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../../../components/global/appContext";
import { useParams } from "react-router-dom";
import ThemeLanguageSkeleton from "./Skeleton";
import useCompare from "../../../../../hooks/useCompare";
import formatKey from "../../../../../utilities/formatKey";
import useLanguageSelector from "../../../../../hooks/useLanguageSelector";
import LanguageSelector from "../../../../../components/components/LanguageSelector";
import {
  useGetThemeLanguage,
  useUpdateThemeLanguage,
} from "../../../../../hooks/queries/themes";
import { useGetLanguageSettings } from "../../../../../hooks/queries/languageSettings";
import ThemeLanguageEmptyState from "./EmptyState";

export default function ThemeLanguageEdit(props) {
  const { t } = useTranslation();
  const appContext = useContext(AppContext);
  let { themeId } = useParams();

  const { data, isError } = useGetThemeLanguage(themeId);
  const { data: languageSettings } = useGetLanguageSettings();
  
  const [fullLanguageSchema, setFullLanguageSchema] = useState(null);
  const [selectedLocale, setSelectedLocale] = useState('en');
  const [currentLocaleSchema, setCurrentLocaleSchema] = useState({});

  // Initialize the selected locale and current schema when data loads
  useEffect(() => {
    if (data) {
      setFullLanguageSchema(data);
      
      // Use shop's default locale, not theme's default locale
      const shopDefaultLocale = languageSettings?.localization?.default_locale || 'en';
      setSelectedLocale(shopDefaultLocale);
      
      // Set current locale schema - try shop's default first, then fallback to theme's default
      const themeDefaultLocale = data.default_locale || 'en';
      if (data.locales && data.locales[shopDefaultLocale]) {
        setCurrentLocaleSchema(data.locales[shopDefaultLocale]);
      } else if (data.locales && data.locales[themeDefaultLocale]) {
        setCurrentLocaleSchema(data.locales[themeDefaultLocale]);
      }
    }
  }, [data, languageSettings]);

  // Update current locale schema when locale changes
  useEffect(() => {
    if (fullLanguageSchema?.locales?.[selectedLocale]) {
      // Locale has existing translations
      setCurrentLocaleSchema(fullLanguageSchema.locales[selectedLocale]);
    } else if (fullLanguageSchema?.locales) {
      // Locale doesn't have translations - create empty schema based on shop's default locale structure
      const shopDefaultLocale = languageSettings?.localization?.default_locale || 'en';
      const themeDefaultLocale = fullLanguageSchema.default_locale || 'en';
      
      // Try shop's default first, then theme's default
      const defaultSchema = fullLanguageSchema.locales[shopDefaultLocale] || fullLanguageSchema.locales[themeDefaultLocale] || {};
      
      // Create empty schema with same structure as default locale
      const emptySchema = {};
      Object.keys(defaultSchema).forEach(categoryKey => {
        emptySchema[categoryKey] = {};
        if (defaultSchema[categoryKey] && typeof defaultSchema[categoryKey] === 'object') {
          Object.keys(defaultSchema[categoryKey]).forEach(fieldKey => {
            emptySchema[categoryKey][fieldKey] = "";
          });
        }
      });
      
      setCurrentLocaleSchema(emptySchema);
    }
  }, [selectedLocale, fullLanguageSchema, languageSettings]);

  function updateLanguageSchemaField(value, categoryKey, fieldKey) {
    // Update the current locale schema
    const newCurrentSchema = {
      ...currentLocaleSchema,
      [categoryKey]: { ...currentLocaleSchema[categoryKey], [fieldKey]: value },
    };
    setCurrentLocaleSchema(newCurrentSchema);

    // Update the full schema
    const newFullSchema = {
      ...fullLanguageSchema,
      locales: {
        ...fullLanguageSchema.locales,
        [selectedLocale]: newCurrentSchema
      }
    };
    setFullLanguageSchema(newFullSchema);
  }

  const updateThemeLanguageMutation = useUpdateThemeLanguage(themeId, {
    onSuccess: (responseData) => {
      appContext.setMessage(t("themes.language.messages.saveSuccess"));
      
      // Update state with new data from response
      setFullLanguageSchema(responseData);
      if (responseData.locales?.[selectedLocale]) {
        // Locale has existing translations
        setCurrentLocaleSchema(responseData.locales[selectedLocale]);
      } else if (responseData.locales) {
        // Locale doesn't have translations - create empty schema based on shop's default locale structure
        const shopDefaultLocale = languageSettings?.localization?.default_locale || 'en';
        const themeDefaultLocale = responseData.default_locale || 'en';
        
        // Try shop's default first, then theme's default
        const defaultSchema = responseData.locales[shopDefaultLocale] || responseData.locales[themeDefaultLocale] || {};
        
        // Create empty schema with same structure as default locale
        const emptySchema = {};
        Object.keys(defaultSchema).forEach(categoryKey => {
          emptySchema[categoryKey] = {};
          if (defaultSchema[categoryKey] && typeof defaultSchema[categoryKey] === 'object') {
            Object.keys(defaultSchema[categoryKey]).forEach(fieldKey => {
              emptySchema[categoryKey][fieldKey] = "";
            });
          }
        });
        
        setCurrentLocaleSchema(emptySchema);
      }
    },
    onError: (e) => {
      appContext.setMessage(
        `${t("themes.language.messages.saveError")}: ${e.response?.data?.join(", ") || e.message}`,
        true
      );
    },
  });

  const hasUnsavedChanges = !useCompare(fullLanguageSchema, data);

  // Get available locales
  const getAvailableLocales = () => {
    // Use supported locales from language settings instead of only theme data
    const supportedLocales = getSupportedLocales();
    
    return supportedLocales.map(locale => ({
      label: getLocaleDisplayName(locale),
      value: locale
    })).sort((a, b) => {
      // Put shop's default locale first
      const shopDefaultLocale = languageSettings?.localization?.default_locale || 'en';
      if (a.value === shopDefaultLocale) return -1;
      if (b.value === shopDefaultLocale) return 1;
      return a.label.localeCompare(b.label);
    });
  };

  const getLocaleDisplayName = (locale) => {
    // Try to find in available_locales from language settings
    if (languageSettings?.available_locales) {
      const localeData = languageSettings.available_locales.find(l => l.code === locale);
      if (localeData) return localeData.name;
    }
    
    // Fallback to uppercase if not found
    return locale.toUpperCase();
  };

  const isInternationalizationEnabled = () => {
    return languageSettings?.localization?.enabled || false;
  };

  const getSupportedLocales = () => {
    return languageSettings?.localization?.supported_locales || ['en'];
  };

  // Get placeholder text (only show for non-default locales)
  const getPlaceholderText = (categoryKey, fieldKey) => {
    const shopDefaultLocale = languageSettings?.localization?.default_locale || 'en';
    
    // Don't show placeholder for default locale
    if (selectedLocale === shopDefaultLocale) {
      return "";
    }

    // Show the value from shop's default locale as placeholder, fall back to theme's default
    const themeDefaultLocale = fullLanguageSchema?.default_locale || 'en';
    const defaultValue = fullLanguageSchema?.locales?.[shopDefaultLocale]?.[categoryKey]?.[fieldKey] ||
                        fullLanguageSchema?.locales?.[themeDefaultLocale]?.[categoryKey]?.[fieldKey];
    return defaultValue || "";
  };

  if (isError) return <p>{t("common.error")}</p>;
  if (!fullLanguageSchema) return <ThemeLanguageSkeleton />;
  if (!Object.keys(currentLocaleSchema).length) return <ThemeLanguageEmptyState />;

  const availableLocales = getAvailableLocales();
  const supportedLocales = getSupportedLocales();
  const defaultLocale = languageSettings?.localization?.default_locale || 'en';
  
  // Handle form submission (triggered by Save Bar)
  const handleSubmit = (e) => {
    e.preventDefault();
    updateThemeLanguageMutation.mutate(fullLanguageSchema);
  };

  // Handle form reset (triggered by Discard button in Save Bar)
  const handleReset = (e) => {
    e.preventDefault();
    setFullLanguageSchema(data);
    if (data?.locales?.[selectedLocale]) {
      // Locale has existing translations
      setCurrentLocaleSchema(data.locales[selectedLocale]);
    } else if (data?.locales) {
      // Locale doesn't have translations - create empty schema based on shop's default locale structure
      const shopDefaultLocale = languageSettings?.localization?.default_locale || 'en';
      const themeDefaultLocale = data.default_locale || 'en';
      
      // Try shop's default first, then theme's default
      const defaultSchema = data.locales[shopDefaultLocale] || data.locales[themeDefaultLocale] || {};
      
      // Create empty schema with same structure as default locale
      const emptySchema = {};
      Object.keys(defaultSchema).forEach(categoryKey => {
        emptySchema[categoryKey] = {};
        if (defaultSchema[categoryKey] && typeof defaultSchema[categoryKey] === 'object') {
          Object.keys(defaultSchema[categoryKey]).forEach(fieldKey => {
            emptySchema[categoryKey][fieldKey] = "";
          });
        }
      });
      
      setCurrentLocaleSchema(emptySchema);
    }
  };

  return (
    <form 
      data-save-bar 
      onSubmit={handleSubmit}
      onReset={handleReset}
    >
    <Page>
      <Layout>
        <Layout.Section>
          <Stack vertical spacing="loose">
            {!isInternationalizationEnabled() && (
              <Banner
                title={t("themes.language.banner.multipleLanguagesNotEnabled.title")}
                status="info"
                action={{
                  content: t("themes.language.banner.multipleLanguagesNotEnabled.action"),
                  url: '/settings/languages'
                }}
              >
                <p>
                  {t("themes.language.banner.multipleLanguagesNotEnabled.description", {
                    locale: getLocaleDisplayName(defaultLocale)
                  })}
                </p>
              </Banner>
            )}

            <Card sectioned>
              <FormLayout>
                <LanguageSelector
                  label={t("themes.language.form.language")}
                  labelHidden={false}
                  showIcon={false}
                  options={availableLocales}
                  value={selectedLocale}
                  onChange={setSelectedLocale}
                  helpText={
                    defaultLocale === selectedLocale 
                      ? t("themes.language.form.defaultLanguageHelp") 
                      : t("themes.language.form.translationHelp", {
                          selectedLocale: getLocaleDisplayName(selectedLocale),
                          defaultLocale: getLocaleDisplayName(defaultLocale)
                        })
                  }
                />
              </FormLayout>
            </Card>


            {Object.keys(currentLocaleSchema).map((categoryKey) => {
              const categoryContent = currentLocaleSchema[categoryKey];
              return (
                <Layout.AnnotatedSection
                  title={t(`themes.language.categories.${categoryKey}`, formatKey(categoryKey))}
                  key={categoryKey}
                  description={
                    <Text>
                      {t("themes.language.accessLanguageTerms", {
                        category: t(`themes.language.categories.${categoryKey}`, formatKey(categoryKey)).toLowerCase(),
                        code: `language.${categoryKey}`
                      })}
                      <Text as="span"><InlineCode>{`language.${categoryKey}`}</InlineCode></Text>
                    </Text>
                  }
                >
                  <Card sectioned>
                    <FormLayout>
                      {Object.keys(categoryContent).map((fieldKey) => (
                        <TextField
                          key={fieldKey}
                          label={t(`themes.language.fields.${fieldKey}`, formatKey(fieldKey))}
                          value={currentLocaleSchema[categoryKey][fieldKey] || ""}
                          onChange={(value) =>
                            updateLanguageSchemaField(value, categoryKey, fieldKey)
                          }
                          autoComplete="off"
                          placeholder={getPlaceholderText(categoryKey, fieldKey)}
                        />
                      ))}
                    </FormLayout>
                  </Card>
                </Layout.AnnotatedSection>
              );
            })}
          </Stack>
        </Layout.Section>
      </Layout>
    </Page>
    </form>
  );
}
